<template>
  <div>
    <div class="header">
      <div class="container-fluid container-boxed">
        <div class="header-body">
          <h6 class="header-pretitle">Detail</h6>

          <h1
            v-if="editingAgency && $userIsAdmin"
            class="header-title"
          >Edit Agency - {{ agency.name }}</h1>
          <h1 v-else-if="editingAgency" class="header-title">{{ agency.name }}</h1>
          <h1 v-else class="header-title">Create Agency</h1>
        </div><!-- / .header-body -->
      </div>
    </div><!-- / .header -->

    <div class="container-fluid container-boxed">
      <template v-if="$userIsAdmin">
        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label class="control-label">Agency Name</label>
              <input
                v-model="stateName"
                v-validate="'required'"
                name="name"
                type="text"
                class="form-control">
              <help-block field="name"/>
            </div><!-- /.form-group -->
          </div>

          <div class="col-md-6 col-lg-3">
            <div class="form-group">
              <label class="control-label">Abbreviation</label>
              <input
                v-model="stateAbbreviation"
                v-validate="'required'"
                name="abbreviation"
                type="text"
                class="form-control text-uppercase">

              <help-block field="abbreviation"/>
            </div><!-- /.form-group -->
          </div>

          <div class="col-md-6 col-lg-3">
            <div class="form-group">
              <label class="control-label">Status</label>
              <multiselect
                v-model="statusSelected"
                :options="statusOptions"
                name="enabled"
                v-validate="'required'"
                data-vv-validate-on="blur"
                data-vv-as="status"
                :class="[errorClass('enabled'), validClass('enabled')]"
                track-by="value"
                label="text"
                placeholder
                :allow-empty="false"
                :searchable="false"
                :show-labels="false"
              />
            </div><!-- /.form-group -->
          </div>
        </div><!-- /.row -->

        <div class="text-right">
          <ladda-button
            :validate="true"
            :click="handleSaveAgency"
            class="btn btn-success btn-sm"
          >Save Changes</ladda-button>
        </div>

        <div class="decorative-divider"></div>
      </template>

      <div v-if="editingAgency">
        <div class="row mb-3">
          <div class="col">
            <h3>Agency Coordinators</h3>
          </div>

          <div class="col-auto">
            <button
              v-if="$userIsAdmin"
              type="button"
              class="btn btn-sm btn-outline-primary"
              data-toggle="modal"
              data-target=".modal-add-user"
            >
              <font-awesome :icon="['far', 'plus']" class="mr-2"/>Add Coordinator
            </button>
          </div>
        </div><!-- /.row -->

        <div class="card">
          <ul class="list-data has-button">
            <li class="data-heading d-none d-xl-block">Name</li>
            <no-results v-if="noCoordinators">No Coordinators</no-results>
            <coordinator-list-item
              v-else
              v-for="coordinator in agency.coordinators"
              :coordinator="coordinator"
              :key="coordinator.id"
            />
          </ul><!-- /.list-data -->
        </div><!-- /.card -->

        <div class="decorative-divider"></div>

        <LogCard v-if="$userIsAdmin" type="agency" />

        <LogDrawer v-if="$userIsAdmin" type="agency" :target="agency"/>

        <ladda-button v-if="$userIsAdmin" :click="downloadCoordinatorLog" class="btn btn-sm btn-warning">
          <font-awesome :icon="['far', 'archive']" fixed-width/> Download Coordinator Log
        </ladda-button>
      </div>
    </div><!-- /.container-fluid -->

    <portal to="modals">
      <add-coordinator-modal/>
    </portal>
  </div>
</template>

<script>
import { stateMapper } from 'mixins/agencies'
import { agencyComputed, agencyMethods } from 'vx/helpers'
import { pick, kebabCase } from 'lodash'
import { handleServerErrors } from 'utils'
import Multiselect from 'vue-multiselect'
import CoordinatorListItem from './AgenciesSingleCoordinatorsListItem'
import AddCoordinatorModal from './AddCoordinatorModal'
import { LogCard, LogDrawer } from 'components/log'

export default {
  mixins: [stateMapper],

  props: {
    id: {
      required: false,
      type: Number,
      default: null
    }
  },

  components: {
    Multiselect,
    CoordinatorListItem,
    AddCoordinatorModal,
    LogCard,
    LogDrawer
  },

  computed: {
    ...agencyComputed,

    /**
     * Get and set the selected status
     */
    statusSelected: {
      get () {
        return this.statusOptions.find(option => option.value === this.agency.enabled)
      },

      set (option) {
        this.stateEnabled = option.value
      }
    },

    /**
     * Determine if we are on the agency create
     */
    creatingAgency () {
      return this.$route.name === 'agencies.create'
    },

    /**
     * Determine if we are on the agency edit route
     */
    editingAgency () {
      return this.$route.name === 'agencies.edit'
    },

    noCoordinators () {
      return this.agency && this.agency.coordinators.length === 0
    }
  },

  data () {
    return {
      statusOptions: [
        {
          value: true,
          text: 'Enabled'
        },
        {
          value: false,
          text: 'Disabled'
        }
      ]
    }
  },

  async created () {
    if (this.creatingAgency) {
      await this.resetAgency()

      return
    }

    try {
      await this.getAgency(this.id)
    } catch (error) {
      this.$router.replace({ name: 'agencies' })
    }
  },

  methods: {
    ...agencyMethods,

    async downloadCoordinatorLog () {
      const response = await this.getCoordinatorLog(this.agency)

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(response.data, `${kebabCase(this.agency.name)}-coordinator-log.csv`)
      } else {
        let anchor = document.createElement('a')

        anchor.href = window.URL.createObjectURL(new Blob([response.data]))
        anchor.setAttribute(
          'download',
          `${kebabCase(this.agency.name)}-coordinator-log.csv`
        )

        document.body.appendChild(anchor)

        anchor.click()
        anchor.remove()
      }
    },

    /**
     * Update or create the agency
     */
    async handleSaveAgency () {
      try {
        let payload = pick(this.agency, [
          'name',
          'abbreviation',
          'enabled'
        ])

        if (this.editingAgency) {
          await this.updateAgency({ agency: this.agency, payload })

          this.$noty.success('Agency has been updated.')
        } else if (this.creatingAgency) {
          await this.storeAgency(payload)

          this.$noty.success('Agency has been created.')

          this.$router.push({ name: 'agencies.edit', params: { id: this.agency.id } })
        }
      } catch (error) {
        handleServerErrors(error, this.$validator)
      }
    }
  }
}
</script>
